import { useTranslation } from '../../../i18n/useTranslation';
import type { LicenseFilterState, LicenseStatus, LicenseType } from '../../../types/manageLicenses';

interface Props {
  filters: LicenseFilterState;
  onChange: (patch: Partial<LicenseFilterState>) => void;
}

export default function LicenseListFilters({ filters, onChange }: Props) {
  const { t } = useTranslation();
  const expiringActive = filters.expiringWithinDays === 30;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-3">
      <input
        type="search"
        value={filters.q}
        onChange={(e) => onChange({ q: e.target.value, page: 1 })}
        placeholder={t('App.Admin.Licenses.LicenseListFilters.SearchPlaceholder')}
        className="border border-neutral-300 rounded px-3 py-2 text-sm w-72"
        aria-label={t('App.Admin.Licenses.LicenseListFilters.SearchAriaLabel')}
      />

      <select
        value={filters.status === 'all' ? '' : String(filters.status)}
        onChange={(e) => onChange({
          status: e.target.value === '' ? 'all' : (Number(e.target.value) as LicenseStatus),
          page: 1,
        })}
        className="border border-neutral-300 rounded px-2 py-2 text-sm"
        aria-label="Status filter"
      >
        <option value="">{t('App.Admin.Licenses.LicenseListFilters.StatusAll')}</option>
        <option value="1">{t('App.Common.LicenseStatus.Active')}</option>
        <option value="2">{t('App.Common.LicenseStatus.Expired')}</option>
        <option value="3">{t('App.Common.LicenseStatus.Revoked')}</option>
        <option value="4">{t('App.Common.LicenseStatus.Suspended')}</option>
      </select>

      <select
        value={filters.type === 'all' ? '' : String(filters.type)}
        onChange={(e) => onChange({
          type: e.target.value === '' ? 'all' : (Number(e.target.value) as LicenseType),
          page: 1,
        })}
        className="border border-neutral-300 rounded px-2 py-2 text-sm"
        aria-label="Type filter"
      >
        <option value="">{t('App.Admin.Licenses.LicenseListFilters.TypeAll')}</option>
        <option value="1">{t('App.Common.LicenseType.Purchase')}</option>
        <option value="2">{t('App.Common.LicenseType.Rental')}</option>
        <option value="3">{t('App.Common.LicenseType.Subscription')}</option>
      </select>

      <button
        type="button"
        onClick={() => onChange({
          expiringWithinDays: expiringActive ? undefined : 30,
          page: 1,
        })}
        className={`px-3 py-2 text-sm border rounded ${
          expiringActive
            ? 'bg-amber-100 border-amber-400 text-amber-800'
            : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50'
        }`}
        aria-pressed={expiringActive}
      >
        {t('App.Admin.Licenses.LicenseListFilters.ExpiringIn30d')}
      </button>
    </div>
  );
}
